<template>
  <div class="stock-correction-list">
    <el-row class="page-buttons">
      <editor-button
        v-show="hasTaskPermissions"
        :disabled="selectedDomainID === null"
        :icon-only="false"
        :loading="loadingSubmit"
        button-translation="stockCorrection.setStock"
        action="primary"
        @click="runSetStockTask()"
      />
      <editor-button v-if="filtersActive" action="clearFilters" @click="clearAllFilters" />
      <editor-button action="cancel" style="float:right" @click="close" />
      <editor-button
        v-show="selectedStockLocationID !== null && reasonStockCorrectionID !== null"
        :icon-only="false"
        :loading="loadingSubmit"
        button-translation="stockCorrection.processStockCorrection"
        action="primary"
        style="float:right;margin-right:10px"
        @click="saveSelection()"
      />
      <editor-button
        v-show="changesMadeOnCurrentPage"
        class="clear-input-button"
        :icon-only="false"
        button-translation="stockCorrection.clearInput"
        action="secondary"
        @click="clearInput()"
      />
    </el-row>
    <pagination
      v-show="loadedData.total > 0"
      :total="loadedData.total"
      :page.sync="listQuery.page"
      :limit.sync="listQuery.limit"
      :disabled="changesMade"
      @pagination="refreshList"
    />
    <div v-if="changesMadeOnCurrentPage" class="validation-errors">
      {{ $t('stockCorrection.clearInputSuggestion') }}
    </div>
    <div v-if="selectedStockLocationID === null || reasonStockCorrectionID === null" class="validation-errors">
      {{ $t('stockCorrection.selectAStockLocation') }}
    </div>
    <div>
      <label class="material-label" style="padding-bottom: 5px">{{ $t('stockCorrection.stockLocationName') }}</label>
    </div>
    <div>
      <el-select
        v-model="selectedStockLocationID"
        :disabled="changesMade"
        :placeholder="$t('stockCorrection.stockLocationName')"
        class="search-select"
        type="select"
        style="width: 20%; padding-bottom: 10px;padding-right:10px"
        @input="handleStockLocationChange($event)"
      >
        <el-option-group v-if="stockLocations.length" :label="$t('stockCorrection.stockLocationName')">
          <el-option
            v-for="stockLocation in stockLocations"
            :key="stockLocation.id"
            :label="stockLocation.name"
            :value="stockLocation.id"
          />
        </el-option-group>
      </el-select>
      <el-collapse-transition>
        <el-alert v-if="errorMsg" :title="errorMsg" type="error" :closable="false" />
      </el-collapse-transition>
      <el-select
        v-model="selectedDomainID"
        :disabled="!selectedStockLocationID || changesMade"
        :placeholder="$t('common.domain')"
        clearable
        class="search-select"
        type="select"
        style="width: 20%; padding-bottom: 10px"
        @input="handleDomainChange($event)"
      >
        <el-option-group v-if="domains.length" :label="$t('common.domain')">
          <el-option v-for="domain in domains" :key="domain.id" :label="domain.name" :value="domain.id" />
        </el-option-group>
      </el-select>
      <el-collapse-transition>
        <el-alert v-if="errorMsg" :title="errorMsg" type="error" :closable="false" />
      </el-collapse-transition>
      <el-select
        v-model="reasonStockCorrectionID"
        :placeholder="$t('stockCorrection.chooseReasonStockCorrection')"
        class="search-select"
        type="select"
        style="width: 20%; margin-left:10px"
      >
        <el-option-group
          v-if="reasonStockCorrections.length"
          :label="$t('stockCorrection.chooseReasonStockCorrection')"
        >
          <el-option
            v-for="reason in reasonStockCorrections"
            :key="reason.id"
            :label="reason.description"
            :value="reason.id"
          />
        </el-option-group>
      </el-select>
      <el-select
        v-show="isReasonTransferStock()"
        v-model="targetStockLocationID"
        :placeholder="$t('stockCorrection.targetStockLocation')"
        :clearable="true"
        class="search-select"
        type="select"
        style="width: 20%; margin-left:10px"
      >
        <el-option-group v-if="stockLocations.length" :label="$t('stockCorrection.targetStockLocation')">
          <el-option
            v-for="stockLocation in stockLocations"
            :key="stockLocation.id"
            :label="stockLocation.name"
            :value="stockLocation.id"
          />
        </el-option-group>
      </el-select>
    </div>
    <el-table
      ref="stockCorrectionList"
      v-loading="loading"
      :data="items"
      border
      fit
      highlight-current-row
      style="width: 100%"
      height="650"
      @row-dblclick="rowDblClick"
    >
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-input
            v-model="search['productCode']"
            :disabled="changesMade"
            class="search"
            size="mini"
            :placeholder="$t('stockCorrection.stockProductCode')"
            clearable
            prefix-icon="el-icon-search"
          />
        </template>
        <el-table-column :label="$t('stockCorrection.stockProductCode')" property="code">
          <template slot-scope="scope">
            <span>{{ scope.row['productCode'] }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-input
            v-model="search['productDescription']"
            :disabled="changesMade"
            class="search"
            size="mini"
            :placeholder="$t('stockCorrection.stockProductDescription')"
            clearable
            prefix-icon="el-icon-search"
          />
        </template>
        <el-table-column :label="$t('stockCorrection.stockProductDescription')" property="description">
          <template slot-scope="scope">
            <span>{{ scope.row['productDescription'] }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}"> </template>
        <el-table-column :label="$t('stock.positionCode')" width="150">
          <template slot-scope="{ row }">
            <MDinput v-model="row.positionCode" max-length="32" @input="handlePositionCodeCorrection(row, $event)" />
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}"> </template>
        <el-table-column :label="$t('stock.stockAvailable')" property="availableQuantityInMeters">
          <template slot-scope="scope">
            <span>{{ scope.row['availableQuantityInMeters'] }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}"> </template>
        <el-table-column :label="$t('stock.stockBlocked')" property="blockedQuantityInMeters">
          <template slot-scope="scope">
            <span>{{ scope.row['blockedQuantityInMeters'] }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}"> </template>
        <el-table-column :label="$t('stockCorrection.correction')" width="300">
          <template slot-scope="{ row }">
            <MDinput
              v-model="row.correction"
              type="number"
              :precision="2"
              :min="-99999"
              placeholder="common.fillInANumber"
              @input="handleInputCorrection(row, $event)"
            />
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}"> </template>
        <el-table-column :label="$t('stockCorrection.remarks')" width="300">
          <template slot-scope="{ row }">
            <MDinput v-model="row.remarks" type="text" @input="handleInputRemarks(row, $event)" />
          </template>
        </el-table-column>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import EditorButton from '@/components/crud/EditorButton';
import MDinput from '@/components/MDinput';
import { entityToStore } from '@/utils/store';
import { closeCurrentTag } from '@/utils/tags';
import CrudTablePersistentState from '@/components/crud/CrudTablePersistentState';
import { hasEditPermissionForCode } from '@/utils/permission';
import { ReasonStockCorrectionOption } from '@/utils/constants';

const debounce = require('lodash.debounce');

const persistentState = new CrudTablePersistentState();

export default {
  name: 'StockCorrectionList',
  components: {
    Pagination,
    EditorButton,
    MDinput
  },
  props: {
    entity: {
      default: '',
      type: String
    },
    onRowDblClick: {
      type: Function
    },
    onEdit: {
      type: Function
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    permissionCode: String
  },
  data() {
    return {
      loadingSubmit: false,
      entityStoreName: entityToStore(this.entity),
      errorMsg: '',
      loadedData: { items: [], total: 0 },
      listQuery: persistentState.getDefaultListQuery(),
      search: persistentState.getDefaultSearch(this.filters),
      total: 0,
      defaultCellAlign: 'left',
      correctionErrors: [],
      items: [],
      selectedStockLocationID: null,
      targetStockLocationID: null,
      changesMadeOnCurrentPage: false,
      selectedDomainID: null,
      reasonStockCorrectionID: null
    };
  },
  computed: {
    hasTaskPermissions() {
      return hasEditPermissionForCode('SetStockTask', this.$store.getters.permissions);
    },
    stockLocations() {
      return this.$store.getters['stockLocation/loadedItems'];
    },
    domains() {
      return this.$store.getters.domains;
    },
    reasonStockCorrections() {
      return this.$store.getters['reasonStockCorrection/loadedItems'].filter(
        reason =>
          reason.reasonStockCorrectionOption === ReasonStockCorrectionOption.ManualCorrection ||
          reason.reasonStockCorrectionOption === ReasonStockCorrectionOption.Block ||
          reason.reasonStockCorrectionOption === ReasonStockCorrectionOption.Transfer
      );
    },
    listParams() {
      const params = {};
      params.query = { ...this.filters };
      params.query = { ...params.query, ...this.listQuery };
      if (Object.keys(this.search).length > 0) {
        params.query = { ...params.query, ...this.search };
      }
      params.options = {};
      return params;
    },
    loading() {
      return this.$store.getters[this.entityStoreName + '/isLoading'];
    },
    numberOfItems() {
      return this.$store.getters['fabric/totalItemsCount'];
    },
    filtersActive() {
      return Object.keys(this.search).some(k => this.search[k]);
    },
    changesMade() {
      return this.changesMadeOnCurrentPage;
    }
  },
  watch: {
    search: {
      deep: true,
      handler: function(value) {
        this.listQuery.page = 1;
        this.debouncedUpdateSearch();
      }
    },
    filters(filters) {
      Object.keys(filters).forEach(key => {
        this.$set(this.search, key, filters[key]);
      });
    },
    listParams(listParameters) {
      persistentState.persistListParams(listParameters);
    }
  },
  created() {
    this.$store.dispatch('reasonStockCorrection/getItems');
    this.$store.dispatch('stockLocation/getItems');
    this.debouncedUpdateSearch = debounce(this.refreshList, 500);
  },
  methods: {
    rowDblClick(row) {
      if (this.onRowDblClick) {
        this.onRowDblClick(row);
      }
      this.$emit('input', row);
    },
    clearInput() {
      this.$store
        .dispatch('notify/confirm', { message: this.$i18n.t('stockCorrection.clearInputWarning') })
        .then(() => {
          this.refreshList();
          this.changesMadeOnCurrentPage = false;
        })
        .catch(() => {});
    },
    refreshList() {
      if (!this.selectedStockLocationID || this.selectedStockLocationID.length === 0) {
        return;
      }
      this.$store
        .dispatch('stockCorrection/readStocksIncludingNonExistent', {
          listParams: this.listParams,
          stockLocationID: this.selectedStockLocationID,
          domainID: this.selectedDomainID
        })
        .then(data => {
          this.loadedData.items = data.data.items;
          this.items = data.data.items;
          this.loadedData.total = data.data.total;
        });
    },
    close() {
      closeCurrentTag(this);
    },
    clearAllFilters() {
      this.$refs.stockCorrectionList.clearFilter();
      this.search = {};
    },
    getTransferStockCorrectionId() {
      const item = this.$store.getters['reasonStockCorrection/loadedItems'].find(
        reason => reason.reasonStockCorrectionOption === ReasonStockCorrectionOption.Transfer
      );
      return item && item.id;
    },
    isReasonTransferStock() {
      const id = this.getTransferStockCorrectionId();
      return id === this.reasonStockCorrectionID;
    },
    getStockLocationName(id) {
      const item = this.$store.getters['stockLocation/loadedItems'].find(i => i.id === id);
      return item && item.name;
    },
    async saveSelection() {
      const reasonStockCorrectionID = this.reasonStockCorrectionID;
      if (!reasonStockCorrectionID) {
        return;
      }
      const stockCorrections = [];
      const transferStockCorrectionID = this.getTransferStockCorrectionId();
      let transferCount = 0;
      this.items.forEach(row => {
        if (row.correction || row.positionCodeChanged) {
          if (transferStockCorrectionID && reasonStockCorrectionID === transferStockCorrectionID) {
            transferCount += 1;
          }
          stockCorrections.push({
            stockID: row.stockID,
            productID: row.productID,
            positionCode: row.positionCode ? row.positionCode : '',
            stockLocationID: this.selectedStockLocationID,
            correction: row.correction ? row.correction : null,
            reasonStockCorrectionID: reasonStockCorrectionID,
            remarks: row.remarks,
            transferToStockLocationId: this.targetStockLocationID
          });
        }
      });
      if (stockCorrections.length > 0) {
        if (transferCount > 0) {
          if (!this.targetStockLocationID) {
            this.$store.dispatch('notify/error', { message: this.$i18n.t('stockCorrection.selectTransferLocation') });
            return;
          }
          if (this.targetStockLocationID === this.selectedStockLocationID) {
            this.$store.dispatch('notify/error', {
              message: this.$i18n.t('stockCorrection.transferToSameLocationError')
            });
            return;
          }
          try {
            const name = this.getStockLocationName(this.targetStockLocationID);
            const message =
              this.$i18n.locale === 'nl'
                ? `${transferCount} stof(fen) overboeken naar ${name}?`
                : `Transfer ${transferCount} fabric(s) to ${name}?`;
            await this.$store.dispatch('notify/confirm', {
              message: message
            });
          } catch {
            return;
          }
        }
        this.loadingSubmit = true;
        this.errorMsg = '';
        try {
          this.$store.dispatch('stockCorrection/submitStockCorrections', stockCorrections).then(() => {
            this.$store.dispatch('notify/success', { message: this.$i18n.t('common.data_updated') });
            this.refreshList();
            this.changesMadeOnCurrentPage = false;
            this.targetStockLocationID = null;
          });
        } catch (err) {
          this.errorMsg = err && (err.message || this.$i18n.t('common.error'));
        } finally {
          this.loadingSubmit = false;
        }
      } else {
        this.$store.dispatch('notify/error', { message: this.$i18n.t('stockCorrection.noInput') });
      }
    },
    handlePositionCodeCorrection(row, positionCode) {
      row.positionCodeChanged = true;
      this.validate(row);
    },
    handleInputCorrection(row, correction) {
      row.correction = correction;
      this.validate(row);
    },
    handleStockLocationChange(inputStockLocationID) {
      this.selectedStockLocationID = inputStockLocationID;
      this.refreshList();
    },
    handleDomainChange(inputDomainID) {
      this.selectedDomainID = inputDomainID;
      this.refreshList();
    },
    handleInputRemarks(row, remarks) {
      row.remarks = remarks;
    },
    validate(row) {
      this.changesMadeOnCurrentPage = true;
      this.$set(row, 'reasonEmpty', !!row.correction || row.positionCodeChanged);
    },
    runSetStockTask() {
      this.loadingSubmit = true;
      this.$store
        .dispatch('stockCorrection/setStockTask', { domainID: this.selectedDomainID })
        .then(() => {
          this.$store.dispatch('notify/success');
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    }
  }
};
</script>
<style lang="scss">
.clear-input-button {
  float: right;
  background-color: #ff8c8c;
  color: white;

  &:hover {
    background-color: #ffb3b3;
    color: white;
  }
}
.validation-errors {
  display: block;
  color: #f56c6c;
  text-align: left;
  top: 100%;
}

.stock-correction-list {
  .edit-input {
    padding-right: 100px;
  }

  .cancel-btn {
    position: absolute;
    right: 15px;
    top: 10px;
  }

  .filter-cell > div {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .hide-overflow {
    .cell {
      white-space: nowrap;
    }
  }

  .search {
    .el-input__inner {
      padding-left: 25px;
      padding-right: 20px;
    }

    .el-input__prefix {
      left: 0;
    }

    .el-input__suffix {
      right: 0;
    }
  }

  .search-select {
    width: 100%;

    .el-input__inner {
      padding-left: 5px;
    }

    .el-input {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .el-input__suffix {
      top: -2px;
      right: 10px;
    }
  }

  .el-table .cell {
    min-height: 3em !important;
  }

  .el-table .cell {
    word-break: break-word;
  }

  .el-table .el-table__row {
    background-color: white;
  }

  .el-table tbody tr:hover .cell {
    color: initial;
  }

  .el-table .input-hidden {
    visibility: hidden;
  }
}
</style>
